import { Injectable } from "@angular/core";
import { TableClient, TableEntityResult } from "@azure/data-tables";
import { AdaptClientConfiguration } from "@common/configuration/adapt-client-configuration";
import { StakeholderServicesService } from "app/features/tools/stakeholder-services.service";
import moment from "moment";
import { AuthAuditFilterBuilder } from "./authAuditFilterBuilder";
import { DataAuditFilterBuilder } from "./dataAuditFilterBuilder";

export interface IAuthAudit {
    Action: string;
    Origin: string;
    Reason: string;
    Result: string;
}

export interface IAuthFilters {
    userName: string;
    startDate: Date;
    endDate: Date;

    actionLogin: boolean;
    actionLogout: boolean;

    resultSuccess: boolean;
    resultFailure: boolean;

    reasonUnknownUsr: boolean;
    reasonIncorrectPswd: boolean;
    reasonOther: boolean;
}

export interface IChatAudit {
    ConversationId: string;
    UserMessage: string;
    AssistantMessage: string;
    TotalChunks?: number;
    UserMessageTimestamp: Date;
    timestamp: Date;
    rowKey: string;
    partitionKey: string;
    etag: string;
}

export interface IDataAudit {
    ChangeType: string;
    Data: string;
    OriginalValues: string;
    EntityId: string;
    EntityType: string;
    PersonId: string;
    UserAgent: string;
}

export interface IDataFilters {
    personId: number;
    organisationId: number;
    startDate: Date;
    endDate: Date;

    changeTypeAdded: boolean;
    changeTypeDeleted: boolean;
    changeTypeModified: boolean;

    dataFilter: string;
    entIdFilter: string;
    entTypeFilter: string;
}

export interface IDataAudit {
    PartitionKey: string;
    RowKey: string;
}

@Injectable({
    providedIn: "root",
})
export class AuditService {
    private authSasToken?: string;
    private dataSasToken?: string;
    private chatSasToken?: string;
    private authTableName = AdaptClientConfiguration.AuthAuditTableName;
    private dataTableName = AdaptClientConfiguration.DataAuditTableName;
    private chatTableName = AdaptClientConfiguration.ChatAuditTableName;
    private tablesUrl = AdaptClientConfiguration.IsCurrentEmbedApiBaseUri
        ? AdaptClientConfiguration.EmbedAzureTablesUrl
        : AdaptClientConfiguration.AltoAzureTablesUrl;
    private maxQuerySize = 1000;

    public constructor(private stakeholderServices: StakeholderServicesService) { }

    public async getFilteredAuths(filters: IAuthFilters) {
        if (!this.authSasToken || !this.sasIsCurrent(this.authSasToken)) {
            this.authSasToken = await this.stakeholderServices.generateTableSas(this.authTableName);
        }

        const filter = new AuthAuditFilterBuilder()
            .setStartDate(filters.startDate)
            .setEndDate(filters.endDate)
            .setUserName(filters.userName)
            .setActions(filters.actionLogin, filters.actionLogout)
            .setReasons(filters.reasonOther, filters.reasonUnknownUsr, filters.reasonIncorrectPswd)
            .setResults(filters.resultSuccess, filters.resultFailure)
            .build();

        const tableClient = this.getTableClient(this.authSasToken, this.authTableName);
        const listResults = tableClient.listEntities<IAuthAudit>({
            queryOptions: { filter },
        });

        let authList: TableEntityResult<IAuthAudit>[] = [];

        const iterator = listResults.byPage({ maxPageSize: this.maxQuerySize });

        for await (const page of iterator) {
            authList = page;
            break;
        }
        return authList;
    }

    public async getFilteredData(filters: IDataFilters) {
        if (!this.dataSasToken || !this.sasIsCurrent(this.dataSasToken)) {
            this.dataSasToken = await this.stakeholderServices.generateTableSas(this.dataTableName);
        }

        const filter = new DataAuditFilterBuilder()
            .setStartDate(filters.startDate)
            .setEndDate(filters.endDate)
            .setPersonId(filters.personId)
            .setChangeTypes(filters.changeTypeAdded, filters.changeTypeModified, filters.changeTypeDeleted)
            .setData(filters.dataFilter)
            .setEntityIds(filters.entIdFilter)
            .setEntityTypes(filters.entTypeFilter)
            .setOrganisationId(filters.organisationId)
            .build();

        const tableClient = this.getTableClient(this.dataSasToken, this.dataTableName);
        const listResults = tableClient.listEntities<IDataAudit>({
            queryOptions: { filter },
        });

        let dataList: TableEntityResult<IDataAudit>[] = [];

        const iterator = listResults.byPage({ maxPageSize: this.maxQuerySize });

        for await (const page of iterator) {
            dataList = page;
            break;
        }
        return dataList;
    }

    public async getChatForConversation(conversationId: string) {
        if (!this.chatSasToken || !this.sasIsCurrent(this.chatSasToken)) {
            this.chatSasToken = await this.stakeholderServices.generateTableSas(this.chatTableName);
        }

        const filter = `ConversationId eq '${conversationId}'`;
        const tableClient = this.getTableClient(this.chatSasToken, this.chatTableName);
        const listResults = tableClient.listEntities<IChatAudit>({
            queryOptions: { filter },
        });

        let allEntities: TableEntityResult<IChatAudit>[] = [];
        const iterator = listResults.byPage({ maxPageSize: this.maxQuerySize });
        for await (const page of iterator) {
            allEntities = page;
            break;
        }

        const chunkEntityMap = new Map<string, TableEntityResult<IChatAudit>>();
        const baseEntities: TableEntityResult<IChatAudit>[] = [];

        for (const entity of allEntities) {
            const chunkMatch = entity.rowKey?.match(/^(.+)_(\d+)$/);
            if (chunkMatch && parseInt(chunkMatch[2], 10) > 0) {
                chunkEntityMap.set(entity.rowKey!, entity);
            } else {
                baseEntities.push(entity);
            }
        }

        for (const baseEntity of baseEntities) {
            if (baseEntity.TotalChunks && baseEntity.TotalChunks > 1) {
                let userMessage = baseEntity.UserMessage ?? "";
                let assistantMessage = baseEntity.AssistantMessage ?? "";

                for (let i = 1; i < baseEntity.TotalChunks; i++) {
                    const chunk = chunkEntityMap.get(`${baseEntity.rowKey}_${i}`);
                    if (chunk) {
                        userMessage += chunk.UserMessage ?? "";
                        assistantMessage += chunk.AssistantMessage ?? "";
                    }
                }

                baseEntity.UserMessage = userMessage;
                baseEntity.AssistantMessage = assistantMessage;
            }
        }

        return baseEntities;
    }

    private sasIsCurrent(sasToken: string) {
        const params = new URLSearchParams(sasToken);
        const expiryTime = params.get("se");
        if (!expiryTime) {
            return false;
        }

        return moment.utc(expiryTime).isAfter(new Date());
    }

    private getTableClient(sasToken: string, tableName: string) {
        return new TableClient(`${this.tablesUrl}?${sasToken}`, tableName);
    }
}
